import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { MapPin, Clock, ArrowRight, Calendar } from 'lucide-react';
import { Button } from '../components/ui/button';
import api from '../utils/api';
import { toast } from 'sonner';

function SearchResults() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);

  const origin = searchParams.get('origin');
  const destination = searchParams.get('destination');
  const date = searchParams.get('date');

  useEffect(() => {
    fetchSchedules();
  }, [origin, destination, date]);

  const fetchSchedules = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/schedules/search?origin=${origin}&destination=${destination}&date=${date}`);
      setSchedules(response.data);
    } catch (error) {
      toast.error('Failed to fetch schedules');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectSchedule = (scheduleId) => {
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Please login to book');
      navigate('/login');
      return;
    }
    navigate(`/seat-selection/${scheduleId}`);
  };

  return (
    <div className="min-h-screen bg-neutral-background">
      <nav className="bg-white/90 backdrop-blur-md border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
            <img 
              src="https://customer-assets.emergentagent.com/job_0cf4ab96-b53e-403e-86bf-50fb1ab117ef/artifacts/no4fnv8f_Screenshot_20260105_125050_Samsung%20Notes.jpg"
              alt="Hasaka Tours & Travels"
              className="h-10 w-auto"
            />
          </div>
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="text-slate-600 hover:text-primary font-medium text-sm uppercase tracking-wider"
          >
            Back to Home
          </Button>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="mb-6 sm:mb-8">
          <h2 className="font-manrope font-bold text-2xl sm:text-3xl lg:text-4xl text-primary mb-2" data-testid="search-results-heading">
            Available Schedules
          </h2>
          <p className="text-sm sm:text-base lg:text-lg text-slate-600 font-public-sans">
            {origin} → {destination} on {new Date(date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-neutral-text-secondary font-public-sans">Searching for schedules...</p>
            </div>
          </div>
        ) : schedules.length === 0 ? (
          <div className="bg-white rounded-sm p-12 text-center shadow-sm border border-slate-200" data-testid="no-schedules">
            <Calendar className="w-16 h-16 text-slate-400 mx-auto mb-4" />
            <p className="text-slate-600 font-public-sans text-lg">No schedules available for this route and date.</p>
            <Button onClick={() => navigate('/')} className="mt-6 bg-primary text-white hover:bg-primary-900 rounded-sm px-8 py-3">
              Search Again
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {schedules.map((schedule) => (
              <div
                key={schedule.schedule_id}
                className="bg-white rounded-sm p-4 sm:p-6 lg:p-8 shadow-sm border border-slate-200 hover:shadow-md transition-shadow duration-300"
                data-testid={`schedule-${schedule.schedule_id}`}
              >
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-8 mb-4">
                      <div>
                        <p className="text-xs sm:text-sm text-slate-500 uppercase tracking-wide font-public-sans mb-1">Departure</p>
                        <p className="font-mono text-xl sm:text-2xl lg:text-3xl font-bold text-primary">{schedule.departure_time}</p>
                      </div>
                      <div className="flex-1 px-4 hidden sm:block">
                        <div className="relative">
                          <div className="border-t-2 border-slate-300 border-dashed"></div>
                          <Clock className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-5 h-5 sm:w-6 sm:h-6 text-slate-400 bg-neutral-background px-1" />
                        </div>
                        <p className="text-center text-xs sm:text-sm text-slate-500 font-public-sans mt-2">
                          {schedule.route.duration_hours} hours
                        </p>
                      </div>
                      <div>
                        <p className="text-xs sm:text-sm text-slate-500 uppercase tracking-wide font-public-sans mb-1">Arrival</p>
                        <p className="font-mono text-xl sm:text-2xl lg:text-3xl font-bold text-primary">{schedule.arrival_time}</p>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-3 sm:gap-6 text-xs sm:text-sm text-slate-600">
                      <div className="flex items-center gap-2">
                        <MapPin className="w-3 h-3 sm:w-4 sm:h-4" />
                        <span className="font-public-sans">{schedule.route.distance_km} km</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-public-sans">Vehicle: {schedule.vehicle.vehicle_number}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-public-sans font-semibold text-secondary">
                          {schedule.available_seats.length} seats available
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="lg:ml-8 text-center lg:text-right">
                    <p className="text-xs sm:text-sm text-slate-500 uppercase tracking-wide font-public-sans mb-2">Price per seat</p>
                    <p className="font-manrope text-2xl sm:text-3xl lg:text-4xl font-bold text-primary mb-3 sm:mb-4">${schedule.price}</p>
                    <Button
                      onClick={() => handleSelectSchedule(schedule.schedule_id)}
                      disabled={schedule.available_seats.length === 0}
                      className="bg-secondary text-white hover:bg-secondary-600 rounded-sm px-6 sm:px-8 py-2 sm:py-3 font-semibold tracking-wide uppercase text-xs sm:text-sm w-full lg:w-auto"
                      data-testid={`select-schedule-${schedule.schedule_id}`}
                    >
                      Select Seats
                      <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 ml-2" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default SearchResults;